"use client";

import { useEffect, useRef, useState, type CSSProperties } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, Menu, X } from "lucide-react";
import { trackEvent } from "@/lib/analytics/track";
import type { AttractionId } from "@/lib/attractions/types";

type MobileNavLink = {
  href: string;
  label: string;
  active: boolean;
};

type MobileNavLanguage = {
  href: string;
  label: string;
  active: boolean;
};

type AttractionMobileNavProps = {
  accountHref: string;
  accountLabel: string;
  alertHref: string;
  alertLabel: string;
  attractionId: AttractionId;
  brandName: string;
  closeLabel: string;
  homeHref: string;
  languages: readonly MobileNavLanguage[];
  languageLabel: string;
  links: readonly MobileNavLink[];
  logo: {
    src: string;
    width: number;
    height: number;
  };
  menuLabel: string;
  navigationLabel: string;
  themeStyle: CSSProperties;
};

export default function AttractionMobileNav({
  accountHref,
  accountLabel,
  alertHref,
  alertLabel,
  attractionId,
  brandName,
  closeLabel,
  homeHref,
  languages,
  languageLabel,
  links,
  logo,
  menuLabel,
  navigationLabel,
  themeStyle,
}: AttractionMobileNavProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open) return;

    const trigger = triggerRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      trigger?.focus();
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="inline-flex size-11 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card xl:hidden"
        aria-label={menuLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="attraction-mobile-navigation"
        onClick={() => setOpen(true)}
      >
        <Menu className="size-5" aria-hidden="true" />
      </button>

      {open ? (
        <>
          <div className="fixed inset-0 z-50 bg-black/50" aria-hidden="true" onClick={() => setOpen(false)} />
          <aside
            ref={dialogRef}
            id="attraction-mobile-navigation"
            role="dialog"
            aria-modal="true"
            aria-label={brandName}
            className="attraction-theme fixed inset-y-0 right-0 z-50 flex w-[min(90vw,24rem)] flex-col overflow-hidden border-l border-border bg-card text-foreground shadow-lg"
            style={themeStyle}
          >
            <div className="h-1 shrink-0 bg-brand-accent" />
            <div className="shrink-0 border-b border-border px-5 pb-3 pt-4 text-left">
              <h2>
                <Link
                  href={homeHref}
                  onClick={() => setOpen(false)}
                  className="flex min-w-0 items-center gap-3 pr-10"
                >
                  <Image
                    src={logo.src}
                    alt=""
                    width={logo.width}
                    height={logo.height}
                    className="h-8 w-auto max-w-10 shrink-0 object-contain"
                  />
                  <span className="truncate font-display text-base font-bold tracking-tight">{brandName}</span>
                </Link>
              </h2>
              <button
                ref={closeRef}
                type="button"
                aria-label={closeLabel}
                onClick={() => setOpen(false)}
                className="absolute right-3 top-3 inline-flex size-10 items-center justify-center rounded-md border border-border bg-background text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
              >
                <X className="size-5" aria-hidden="true" />
              </button>
            </div>

            <nav className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 py-2" aria-label={navigationLabel}>
              {links.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  aria-current={link.active ? "page" : undefined}
                  className="flex min-h-11 items-center rounded-lg px-4 text-base font-semibold text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground aria-[current=page]:bg-secondary aria-[current=page]:text-foreground"
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="shrink-0 border-t border-border px-4 py-3">
              {languages.length > 1 ? (
                <div className="mb-3">
                  <p className="px-1 font-utility text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
                    {languageLabel}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {languages.map((language) => (
                      <a
                        key={language.label}
                        href={language.href}
                        onClick={() => setOpen(false)}
                        hrefLang={language.label.toLowerCase()}
                        aria-current={language.active ? "page" : undefined}
                        className="inline-flex min-h-10 min-w-12 items-center justify-center rounded-full border border-border px-3 text-xs font-bold uppercase transition-colors hover:bg-secondary aria-[current=page]:border-brand-accent aria-[current=page]:bg-secondary aria-[current=page]:text-foreground"
                      >
                        {language.label}
                      </a>
                    ))}
                  </div>
                </div>
              ) : null}
              <Link
                href={accountHref}
                onClick={() => setOpen(false)}
                className="flex min-h-11 items-center justify-center rounded-lg px-4 text-sm font-semibold text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                {accountLabel}
              </Link>
              <Link
                href={alertHref}
                onClick={() => {
                  trackEvent("click_create_alert", { attraction_id: attractionId, location: "mobile_nav" });
                  setOpen(false);
                }}
                className="mt-2 flex min-h-12 items-center justify-center gap-2 rounded-lg bg-brand-action px-4 text-sm font-bold text-brand-action-foreground transition-opacity hover:opacity-90"
              >
                {alertLabel}
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </div>
          </aside>
        </>
      ) : null}
    </>
  );
}
